import { cn } from '@shared-ui/utils'
import { useTheme } from 'app/providers/theme/index'
import { useTranslation } from 'react-i18next'
import { BrightnessIcon } from 'shared/assets/icons'
import { BrightnessIconFilled } from 'shared/assets/icons/Icons'

/**
 * Theme switcher that crossfades the original brightness icons — the filled
 * blue sun (BrightnessIconFilled) in light mode and the grey moon
 * (BrightnessIcon) in dark mode — keeping each icon's own fill. The outgoing
 * icon rotates and scales out while the incoming one rotates and scales in, so
 * toggling reads as the sun setting / moon rising. Reduced-motion is respected.
 */
export const ThemeToggle = () => {
    const { theme, toggleTheme } = useTheme()
    const { t } = useTranslation(['shared'])
    const isDark = theme === 'dark'

    const iconBase =
        'absolute inline-flex items-center justify-center transition-all duration-500 ease-in-out motion-reduce:transition-none [&>svg]:h-8 [&>svg]:w-8'

    return (
        <button
            type="button"
            onClick={toggleTheme}
            aria-label={t('shared:layout.theme-switcher')}
            aria-pressed={isDark}
            className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-inactive/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inactive/40"
        >
            {/* Sun — light mode */}
            <span
                aria-hidden="true"
                className={cn(
                    iconBase,
                    isDark
                        ? 'rotate-90 scale-0 opacity-0'
                        : 'rotate-0 scale-100 opacity-100'
                )}
            >
                <BrightnessIconFilled />
            </span>

            {/* Moon — dark mode */}
            <span
                aria-hidden="true"
                className={cn(
                    iconBase,
                    isDark
                        ? 'rotate-0 scale-100 opacity-100'
                        : '-rotate-90 scale-0 opacity-0'
                )}
            >
                <BrightnessIcon />
            </span>
        </button>
    )
}
